import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_VERSION } from '../version';
import { login as localLogin } from '../lib/localAuth';
import { useAuth } from '../AuthContext';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';

// Registration is permanently disabled (operator directive 2026-08-17):
// accounts are provisioned ONLY by the administrator. Sign-in is the sole
// auth surface this page offers.

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await localLogin(email, password);
      refresh();
      navigate('/portfolio');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <div className="flex flex-col items-center gap-2 rounded-t-lg bg-foreground px-6 py-8 text-background">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-background/15 backdrop-blur">
            <span className="text-xl font-bold text-background">TX</span>
          </div>
          <span className="font-display text-2xl font-bold tracking-tight">Titan-X</span>
          <span className="text-sm opacity-70">Algorithmic Trading Platform</span>
        </div>

        <CardContent className="p-6 space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="login-email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground mt-4">
            Access by administrator provisioning only. Secured by self-hosted
            authentication on Titan-X infrastructure.
          </p>
        </CardContent>

        <div className="border-t px-6 py-3 text-center">
          <span className="text-xs text-muted-foreground">Titan-X v{APP_VERSION}</span>
        </div>
      </Card>
    </div>
  );
};

export default Login;
